import { ChevronDown, TriangleAlert, X } from 'lucide-react';
import React, { ReactNode, UIEvent, forwardRef, useRef, useState } from 'react';

import { Command, CommandInput, CommandItem, CommandList, Input, Spinner, cn } from '@column/ui';

const MATCH_MARK_CLASS = 'rounded-[2px] bg-blue-100 text-foreground dark:bg-blue-500/25';

export const highlightMatch = (text: string, query: string): ReactNode => {
  const q = query.trim();
  if (!q) {
    return text;
  }
  const lowerText = text.toLowerCase();
  const lowerQuery = q.toLowerCase();
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (let idx = lowerText.indexOf(lowerQuery); idx !== -1; idx = lowerText.indexOf(lowerQuery, cursor)) {
    if (idx > cursor) {
      parts.push(text.slice(cursor, idx));
    }
    parts.push(
      <mark key={idx} className={MATCH_MARK_CLASS}>
        {text.slice(idx, idx + q.length)}
      </mark>
    );
    cursor = idx + q.length;
  }
  if (cursor === 0) {
    return text;
  }
  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }
  return <>{parts}</>;
};

// Renders a server search fragment, where matches are wrapped in `<em>…</em>`.
export const renderMatchFragment = (fragment: string): ReactNode => {
  const parts = fragment.split(/(<em>.*?<\/em>)/g);
  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/^<em>(.*?)<\/em>$/);
        return match ? (
          <mark key={i} className={MATCH_MARK_CLASS}>
            {match[1]}
          </mark>
        ) : (
          part
        );
      })}
    </>
  );
};

const TRANSITION_OPEN = 'duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]';
const TRANSITION_CLOSE = 'duration-[500ms] ease-[cubic-bezier(0.32,0.72,0,1)]';

const CardCopyRow: React.FC<{ row: AccountCardRow }> = ({ row }) => (
  <div className="@container/copy-row border-t border-gray-150 dark:border-white/[0.08]">
    <div className="grid items-start leading-4 @tablet/copy-row:grid-cols-[minmax(auto,220px)_auto] @tablet/copy-row:gap-4">
      <div className="px-4 pt-4 text-sm leading-4 font-medium whitespace-nowrap text-gray-600 @tablet/copy-row:p-4 dark:text-gray-300">
        {row.label}
      </div>
      <div className="px-1 pt-1 pb-2 @tablet/copy-row:py-2 @tablet/copy-row:pr-2 @tablet/copy-row:pl-6">
        <Input
          variant="copy"
          inputSize="sm"
          readOnly
          value={row.value}
          showCopyButton
          className={cn(
            '[&_input]:bg-transparent [&_input]:text-muted-foreground [&_input]:shadow-none [&_input]:[--input-border-color:transparent]',
            'hover:[&_input]:bg-white hover:[&_input]:shadow-[inset_0_1px_1px_rgb(0_0_0/0.075),inset_0_2px_4px_rgb(0_0_0/0.025)] hover:[&_input]:[--input-border-color:rgb(43_49_67/0.125)] dark:hover:[&_input]:bg-input/30',
            '[&_[aria-label=Copy]]:opacity-0 [&_[aria-label=Copy]]:transition-opacity focus-within:[&_[aria-label=Copy]]:opacity-100 hover:[&_[aria-label=Copy]]:opacity-100',
            row.monospaced && '[&_input]:font-mono',
            row.emphasized && '[&_input]:font-medium [&_input]:text-foreground'
          )}
        />
      </div>
    </div>
  </div>
);

// `inert` isn't in this @types/react version yet.
const inertWhenHidden = (hidden: boolean) =>
  ({ inert: hidden ? '' : undefined }) as unknown as React.HTMLAttributes<HTMLDivElement>;

export const FadeSwap: React.FC<{ visible: boolean; className?: string; children: ReactNode }> = ({
  visible,
  className,
  children,
}) => (
  <div
    aria-hidden={!visible}
    {...inertWhenHidden(!visible)}
    className={cn(
      'transition-opacity motion-reduce:transition-none',
      visible ? TRANSITION_CLOSE : TRANSITION_OPEN,
      visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      className
    )}
  >
    {children}
  </div>
);

export interface AccountCardRow {
  label: string;
  value: string;
  monospaced?: boolean;
  emphasized?: boolean;
}

export interface SelectedAccountCardProps {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  rows: AccountCardRow[];
  onClear: () => void;
  hasError?: boolean;
  isLoading?: boolean;
  alert?: string;
  className?: string;
}

// While collapsing, keeps rendering the last open content so it fades out with the card instead of emptying.
export const SelectedAccountCard: React.FC<SelectedAccountCardProps> = ({
  open,
  title,
  subtitle,
  icon,
  rows,
  onClear,
  hasError,
  isLoading,
  alert,
  className,
}) => {
  const snapshot = useRef({ title, subtitle, icon, rows, hasError, isLoading, alert });
  if (open) {
    snapshot.current = { title, subtitle, icon, rows, hasError, isLoading, alert };
  }
  const view = open ? { title, subtitle, icon, rows, hasError, isLoading, alert } : snapshot.current;

  return (
    <div
      aria-hidden={!open}
      {...inertWhenHidden(!open)}
      className={cn(
        'grid grid-cols-1 overflow-hidden rounded-lg bg-card text-card-foreground shadow-surface-raised',
        'dark:bg-gray-800 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]',
        'transition-all motion-reduce:transition-none',
        open ? TRANSITION_OPEN : TRANSITION_CLOSE,
        open ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0',
        (view.hasError || view.alert) &&
          'shadow-[0_0_0_1px_var(--color-destructive)] ring-[3px] ring-destructive/20 dark:shadow-[0_0_0_1px_var(--color-destructive)] dark:ring-destructive/40',
        className
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {!view.isLoading && view.icon && (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-400 shadow-[0_0_0_1px_var(--color-gray-150)] dark:bg-white/5 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]">
                {view.icon}
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-medium text-foreground">
                {view.isLoading ? 'Loading…' : view.title}
              </span>
              {!view.isLoading && view.subtitle && (
                <span className="truncate text-xs text-muted-foreground">{view.subtitle}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear selection"
            className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
          >
            <X className="size-4" />
          </button>
        </div>

        {!view.isLoading && view.rows.map((row) => <CardCopyRow key={row.label} row={row} />)}

        {view.alert && (
          <div className="flex items-center gap-2 border-t border-gray-100 bg-destructive/5 px-4 py-2.5 text-xs font-medium text-destructive dark:border-white/[0.08]">
            <TriangleAlert className="size-3.5 shrink-0" />
            <span>{view.alert}</span>
          </div>
        )}
      </div>
    </div>
  );
};

interface EmptyAccountTriggerProps extends React.ComponentProps<'button'> {
  label: string;
  hasError?: boolean;
}

export const EmptyAccountTrigger = forwardRef<HTMLButtonElement, EmptyAccountTriggerProps>(
  ({ label, hasError, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-invalid={hasError || undefined}
      className={cn(
        'flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-lg bg-white px-4 py-2',
        'text-sm font-medium text-gray-400 shadow-btn-secondary transition-[color,box-shadow,opacity] duration-150 outline-none',
        'hover:shadow-btn-secondary-hover focus-visible:shadow-[0_0_0_1px_var(--color-blue-500)] focus-visible:ring-[3px] focus-visible:ring-blue-500/15',
        'dark:bg-input/30 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.25)]',
        // The popover opens on top of the trigger; hide it so their shadows don't stack.
        'data-[state=open]:opacity-0',
        hasError &&
          'bg-destructive/[0.025] shadow-[0_0_0_1px_var(--color-destructive)] ring-[3px] ring-destructive/20 hover:shadow-[0_0_0_1px_var(--color-destructive)] dark:bg-destructive/5 dark:shadow-[0_0_0_1px_var(--color-destructive)] dark:ring-destructive/40 dark:hover:shadow-[0_0_0_1px_var(--color-destructive)]',
        className
      )}
      {...props}
    >
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
    </button>
  )
);
EmptyAccountTrigger.displayName = 'EmptyAccountTrigger';

export interface EntitySearchOption {
  id: string;
  primary: ReactNode;
  secondary?: ReactNode;
  trailingPrimary?: ReactNode;
  trailingSecondary?: ReactNode;
  disabled?: boolean;
  adornment?: ReactNode;
}

export interface EntitySearchCommandProps {
  options: EntitySearchOption[];
  isLoading?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  onSearch: (query: string) => void;
  onSelect: (id: string) => void;
  onScrollEnd?: () => void;
  footer?: ReactNode;
}

export const EntitySearchCommand: React.FC<EntitySearchCommandProps> = ({
  options,
  isLoading,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results found',
  onSearch,
  onSelect,
  onScrollEnd,
  footer,
}) => {
  const [query, setQuery] = useState('');

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 64) {
      onScrollEnd?.();
    }
  };

  return (
    <Command
      shouldFilter={false}
      className="bg-transparent [&_[data-slot=command-input-wrapper]]:h-10 [&_[data-slot=command-input-wrapper]]:px-4"
    >
      <CommandInput
        value={query}
        onValueChange={(value) => {
          setQuery(value);
          onSearch(value);
        }}
        placeholder={searchPlaceholder}
      />
      <CommandList onScroll={handleScroll} className="p-1">
        {options.length === 0 && !isLoading && (
          <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        )}
        {options.map((option) => (
          <CommandItem
            key={option.id}
            value={option.id}
            data-cy={`dropdown-option-${option.id}`}
            disabled={option.disabled}
            onSelect={() => !option.disabled && onSelect(option.id)}
            className="flex items-start gap-3"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate">{option.primary}</span>
              {option.secondary && (
                <span className="truncate text-xs font-normal text-muted-foreground">{option.secondary}</span>
              )}
            </div>
            {(option.trailingPrimary || option.trailingSecondary) && (
              <div className="flex shrink-0 flex-col items-end gap-0.5 pl-6 text-right">
                {option.trailingPrimary && <span className="whitespace-nowrap">{option.trailingPrimary}</span>}
                {option.trailingSecondary && (
                  <span className="text-xs font-normal whitespace-nowrap text-muted-foreground">
                    {option.trailingSecondary}
                  </span>
                )}
              </div>
            )}
            {option.adornment}
          </CommandItem>
        ))}
        {isLoading && (
          <div className="flex justify-center py-3">
            <Spinner />
          </div>
        )}
      </CommandList>
      {footer}
    </Command>
  );
};
